/**
 * `Bus.ExitHiddenState` — the squash-stretch a hidden bus plays the moment its
 * colour is shown, read from `Bus$$ExitHiddenState__0x20f91bc.c`.
 *
 * `:37` kills `_hiddenSequence` (0x148) and `:42` makes a new `Sequence`; then
 * `:86-133`, for `headPart` (0x98), `tailPart` (0xA0) and
 * `middleBody.transform` (0xA8, a `BusBodyMesh` component, hence the
 * `get_transform` at `:118` / `:129`):
 *
 *     Insert(0,     DOScaleY(1.5, 0.179))      // 0x3fc00000, 0x3e374bc7
 *     Insert(0.179, DOScaleY(1,   0.179))      // 0x3f800000, 0x3e374bc7
 *
 * The material tween (`PlayRevealMaterialTween`, inlined from `:148`) goes into
 * the **same** sequence at 0, so the two share one clock — `RevealSequence.
 * elapsed` in `SessionView`. This file is the scale half; `palette.
 * revealMaterialAt` is the material half.
 *
 * ❓ `:52-70` is a fallback branch: when `headPart` **or** `tailPart` is null
 * the same pair of tweens goes onto `meshRenderers[0].transform` (0x20 → +0x20)
 * and `middleBody` is left alone. The prefab export strips `Bus`'s fields, so
 * whether the shipped prefabs ever take it cannot be read; the port takes the
 * main branch, which every `ConveyorBox (S|M|L).prefab` supports — all three
 * carry `Parts/Head`, `Parts/Middle`, `Parts/Tail`, each with a `BusBodyMesh`
 * (checklist §10).
 */

/** 🔍 `0x3fc00000` — the end value of the first `DOScaleY`. */
export const REVEAL_BOUNCE_SCALE_Y = 1.5

/** 🔍 `0x3e374bc7` — the duration of each half; the second is inserted at this offset. */
export const REVEAL_BOUNCE_HALF_SECONDS = 0.179

/**
 * The Y scale of the three body parts `elapsed` seconds into the sequence.
 *
 * Up from 1 to 1.5 over the first 0.179 s, back to 1 over the next 0.179 s, 1
 * thereafter. The second tween starts from whatever the first left, which at
 * exactly 0.179 s is 1.5 — the two halves meet.
 *
 * ❓ PLACEHOLDER — ease. Neither `DOScaleY` calls `SetEase`, so
 * `DOTween.defaultEaseType` applies, and `Resources/DOTweenSettings.asset` was
 * exported with every serialised field stripped — the same unknown as the
 * material tween's, and the same choice: linear. See checklist §10.
 *
 * `null` is "no sequence running": scale 1.
 */
export function revealScaleYAt(elapsed: number | null): number {
  if (elapsed === null || elapsed <= 0) return 1
  const half = REVEAL_BOUNCE_HALF_SECONDS
  if (elapsed < half) return 1 + (REVEAL_BOUNCE_SCALE_Y - 1) * (elapsed / half)
  if (elapsed < 2 * half) {
    return REVEAL_BOUNCE_SCALE_Y - (REVEAL_BOUNCE_SCALE_Y - 1) * ((elapsed - half) / half)
  }
  return 1
}
